import React, { useState, useEffect } from "react";
import styles from "../../components/forms/forms.module.css";
import onboardingStyles from "./companyOnboading.module.css";
import { tWithKeyAttr as t } from "../../i18nHelper";
import { useSetupRecurringPaymentMutation, useCompanyPaymentMutation } from "../../services/api/onboarding.api";
import i18n from "../../i18n";
import lockPaymentIcon from "../../images/lock_protection_icon.svg";

type OnboardingPaymentStepProps = {
  onPaymentSuccess?: () => void;
  planId?: number;
  paymentPeriodTypeId?: number;
  contactFullName?: string;
  // Set once payment has already succeeded once, so the card can't be charged again.
  disabled?: boolean;
} & (
  | { companyGuid: string; companyId?: never }   // anonymous onboarding
  | { companyId: number; companyGuid?: never }   // authenticated (own company or superadmin)
);

const OnboardingPaymentStep: React.FC<OnboardingPaymentStepProps> = ({
  onPaymentSuccess,
  planId,
  paymentPeriodTypeId,
  contactFullName,
  disabled,
  ...identifierProps
}) => {
  const companyGuid = "companyGuid" in identifierProps ? identifierProps.companyGuid : undefined;
  const companyId = "companyId" in identifierProps ? identifierProps.companyId : undefined;
  const [cardDetails, setCardDetails] = useState({
    cardOwnerName: "",
    cardOwnerTaz: "",
    cardNumber: "",
    cardDateMMyy: "",
    cardCvv: "",
  });
  const [cardError, setCardError] = useState<string | null>(null);

  // Set default value for cardOwnerName from formik values
  useEffect(() => {
    if (contactFullName && !cardDetails.cardOwnerName) {
      setCardDetails((prev) => ({ ...prev, cardOwnerName: contactFullName }));
    }
  }, [contactFullName]);

  const [setupRecurringPayment, { isLoading: isAnonymousPaymentLoading }] =
    useSetupRecurringPaymentMutation();
  const [companyPayment, { isLoading: isAuthPaymentLoading }] =
    useCompanyPaymentMutation();
  const isRecurringPaymentLoading = isAnonymousPaymentLoading || isAuthPaymentLoading;

  // Format card number with spaces every 4 digits for display
  const formatCardNumber = (value: string) => {
    const cleaned = value.replace(/\s/g, "");
    const chunks = cleaned.match(/.{1,4}/g);
    return chunks ? chunks.join(" ") : cleaned;
  };

  // Format expiry date as MM/YY
  const formatExpiryDate = (value: string) => {
    const cleaned = value.replace(/\//g, "");
    if (cleaned.length >= 2) {
      return cleaned.slice(0, 2) + "/" + cleaned.slice(2, 4);
    }
    return cleaned;
  };

  // Handle card details change
  const handleCardDetailChange = (field: string, value: string) => {
    setCardDetails((prev) => ({ ...prev, [field]: value }));
    setCardError(null);
  };

  // Validate card details
  const validateCardDetails = () => {
    if (!cardDetails.cardOwnerName.trim()) {
      setCardError(t("CARD_OWNER_NAME_REQUIRED"));
      return false;
    }
    if (!cardDetails.cardOwnerTaz.trim()) {
      setCardError(t("CARD_OWNER_ID_REQUIRED"));
      return false;
    }
    if (!cardDetails.cardNumber.trim() || cardDetails.cardNumber.length < 15) {
      setCardError(t("CARD_NUMBER_INVALID"));
      return false;
    }
    if (
      !cardDetails.cardDateMMyy.trim() ||
      !/^\d{4}$/.test(cardDetails.cardDateMMyy)
    ) {
      setCardError(t("CARD_EXPIRY_INVALID"));
      return false;
    }
    // Check if card has expired
    const [month, year] = cardDetails.cardDateMMyy.match(/../g) || [];
    if (month && year) {
      const expiry = new Date(2000 + parseInt(year), parseInt(month) - 1);
      if (expiry < new Date()) {
        setCardError(t("CARD_EXPIRED"));
        return false;
      }
    }
    if (cardDetails.cardCvv && !/^\d{3,4}$/.test(cardDetails.cardCvv)) {
      setCardError(t("CARD_CVV_INVALID"));
      return false;
    }
    return true;
  };

  // Handle direct payment submission
  const handleDirectPaymentSubmit = async () => {
    if (disabled) {
      return;
    }
    if (!validateCardDetails()) {
      return;
    }

    try {
      if (!companyGuid && !companyId) {
        setCardError(t("COMPANY_NOT_FOUND_RETURN_TO_FIRST_STEP"));
        // console.error("[OnboardingPaymentStep] Missing company identifier", { companyGuid, companyId, planId, paymentPeriodTypeId });
        return;
      }
      if (!planId) {
        setCardError(t("MISSING_REQUIRED_PLAN_INFO"));
        // console.error("[OnboardingPaymentStep] Missing planId", { companyGuid, companyId, planId, paymentPeriodTypeId });
        return;
      }
      if (!paymentPeriodTypeId) {
        setCardError(t("MISSING_REQUIRED_PERIOD_INFO"));
        // console.error("[OnboardingPaymentStep] Missing paymentPeriodTypeId", { companyGuid, companyId, planId, paymentPeriodTypeId });
        return;
      }

      const slikaObject = {
        cardOwnerName: cardDetails.cardOwnerName,
        cardOwnerTaz: cardDetails.cardOwnerTaz,
        cardNumber: cardDetails.cardNumber,
        cardDateMMyy: formatExpiryDate(cardDetails.cardDateMMyy),
        cardCvv: cardDetails.cardCvv || undefined,
      };

      const result = companyId
        ? await companyPayment({ plansId: planId, paymentsPeriodTypesId: paymentPeriodTypeId, companyId, slikaObject }).unwrap()
        : await setupRecurringPayment({ plansId: planId, paymentsPeriodTypesId: paymentPeriodTypeId, companyGuid: companyGuid!, slikaObject }).unwrap();

      // Check for errors in the response
      if (result?.d?.Errors && result.d.Errors.length > 0) {
        const firstError = result.d.Errors[0];
        const errorMessage = firstError.Error || t("PAYMENT_ERROR_OCCURRED");
        // console.error("[OnboardingPaymentStep] Payment API returned errors:", result.d.Errors, "planId used:", planId);
        // setCardError(errorMessage);
        setCardError(t("PAYMENT_ERROR_OCCURRED"));
        return;
      }

      // Payment successful - navigate to success page
      if (onPaymentSuccess) {
        // Construct success URL with company details
        onPaymentSuccess();
      }
    } catch (err: any) {
      // console.error("[OnboardingPaymentStep] Payment API error:", err, "planId used:", planId, "paymentPeriodTypeId:", paymentPeriodTypeId, "companyGuid:", companyGuid);
      const errorMessage =
        err?.data?.message || err?.message || t("PAYMENT_ERROR_OCCURRED");
      setCardError(errorMessage);
    }
  };

  return (
    <div className={`${styles.form}`}>
      <ul
        className={`${styles.form__list} ${styles.form__list_type_oneColumn}`}
      >
        <li>
          <label className={styles.form__label}>{t("CARD_OWNER_NAME")}</label>
          <input
            type="text"
            value={cardDetails.cardOwnerName}
            onChange={(e) =>
              handleCardDetailChange("cardOwnerName", e.target.value)
            }
            placeholder={t("CARD_OWNER_NAME_PLACEHOLDER")}
            className={styles.form__input}
            required
          />
        </li>

        <li>
          <label className={styles.form__label}>{t("CARD_OWNER_ID")}</label>
          <input
            type="text"
            value={cardDetails.cardOwnerTaz}
            onChange={(e) =>
              handleCardDetailChange("cardOwnerTaz", e.target.value)
            }
            placeholder={t("CARD_OWNER_ID_PLACEHOLDER")}
            className={styles.form__input}
            required
          />
        </li>

        <li>
          <label className={styles.form__label}>{t("CARD_NUMBER_ONBOARDING")}</label>
          <input
            type="text"
            value={formatCardNumber(cardDetails.cardNumber)}
            onChange={(e) =>
              handleCardDetailChange(
                "cardNumber",
                e.target.value.replace(/\D/g, "")
              )
            }
            placeholder={t("CARD_NUMBER_PLACEHOLDER")}
            className={styles.form__input}
            maxLength={19}
            required
            dir={'ltr'}
              style={i18n.language === 'he' ? { textAlign: 'end' } : {}}
          />
        </li>

        <li>
          <label className={styles.form__label}>{t("CARD_EXPIRY_DATE")}</label>
          <input
            type="text"
            value={formatExpiryDate(cardDetails.cardDateMMyy)}
            onChange={(e) =>
              handleCardDetailChange(
                "cardDateMMyy",
                e.target.value.replace(/\D/g, "")
              )
            }
            placeholder={t("CARD_EXPIRY_PLACEHOLDER")}
            className={styles.form__input}
            maxLength={5}
            required
          />
        </li>

        <li>
          <label className={styles.form__label}>{t("CARD_CVV")}</label>
          <input
            type="text"
            value={cardDetails.cardCvv}
            onChange={(e) =>
              handleCardDetailChange(
                "cardCvv",
                e.target.value.replace(/\D/g, "")
              )
            }
            placeholder={t("CARD_CVV_PLACEHOLDER")}
            className={styles.form__input}
            maxLength={4}
          />
        </li>
      </ul>

      {cardError && (
        <p className={`error`}>
          {cardError}
        </p>
      )}

      <button
        type="button"
        onClick={() => handleDirectPaymentSubmit()}
        className={`button ${onboardingStyles.paymentSubmitBtn}`}
        disabled={isRecurringPaymentLoading || disabled}
      >
        {isRecurringPaymentLoading ? t("PROCESSING") : t("SUBMIT_PAYMENT")}
      </button>

      <div className={onboardingStyles.paymentSecurityText}>
        <img src={lockPaymentIcon} alt="" className={onboardingStyles.lockPaymentIcon} />
        <span>{t("PROTECTED_PAYMENT_ONBOARDING")}</span>
      </div>
      
      <div className={onboardingStyles.cardVerificationText}>
        {t("CARD_VERIFICATION_TEMP_CHARGE_MESSAGE")}
      </div>
    </div>
  );
};

export default OnboardingPaymentStep;
